import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useArchitecturesForJourneyMap } from '../../hooks/related-by-tag-hooks';

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--hbr-color-neutral-border-default, #d9dde3)',
  borderRadius: 12,
  padding: '16px 20px',
  background: 'var(--hbr-color-neutral-bg-default, #fff)',
};
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 12,
  marginTop: 12,
};
const cardStyle: React.CSSProperties = {
  display: 'block',
  border: '1px solid var(--hbr-color-neutral-border-default, #d9dde3)',
  borderRadius: 10,
  padding: '12px 14px',
  textDecoration: 'none',
  color: 'inherit',
};
const clamp: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  margin: '4px 0 0',
  fontSize: 13,
  color: 'var(--hbr-color-neutral-text-secondary, #5b6676)',
};

/**
 * True once the element has scrolled near the viewport. The related lists cost three requests, and the
 * architecture page is built to load with one, so they wait until someone gets to the bottom of the page.
 */
export const useNearViewport = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return { ref, near };
};

/** Architectures that share a tag with a journey map. Each opens its architecture page in this app. */
export const RelatedArchitectures: React.FC<{ mapId: number | null | undefined; className?: string }> = ({ mapId, className }) => {
  const { data } = useArchitecturesForJourneyMap(mapId);
  if (!data.length) return null;
  return (
    <section className={className} style={panelStyle} aria-label="Architectures related to this journey">
      <h3 style={{ margin: 0, fontSize: 16 }}>Related architectures</h3>
      <div style={gridStyle}>
        {data.map((a) => (
          <Link key={a.id} to={a.to} style={cardStyle}>
            <strong style={{ fontSize: 14 }}>{a.name}</strong>
            {a.description && <p style={clamp}>{a.description}</p>}
          </Link>
        ))}
      </div>
    </section>
  );
};
